import React, { FC, useState } from 'react';

import { Button, Input, Popover } from 'antd';
import axios from 'axios';
import { useDispatch } from 'react-redux';

import {
  axiosConfigAuth,
  handleResponseError,
} from '../../../../api/middleware';
import { API_GET_FILTER_LIST } from '../../../../api/urls';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';

interface IProps {
  list?: Array<{
    id: number;
    name: string;
  }>;
  showListPlus?: boolean | undefined;
  id: number;
  setList: (checkeds: any[]) => void;
  setMainList?: (list: any[]) => void;
}

const ListTextSelector: FC<IProps> = ({
  id,
  list,
  setList,
  setMainList,
  showListPlus,
}) => {
  const dispatch = useDispatch();
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);

  const [ open, setOpen ] = useState(false);
  const [ listText, setListText ] = useState('');
  const [ isLoading, setIsLoading ] = useState(false);

  // Нормализация строки для сравнения: убираем лишние пробелы, &nbsp;, приводим к нижнему регистру
  const normalizeString = (str: string): string => {
    return str
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  };

  // Извлекает название без ID в скобках в конце
  // Например: "МК / ЦВЕТЫ ИЗ ТКАНИ / КУРСЫ / 04.02 (706957811)" -> "МК / ЦВЕТЫ ИЗ ТКАНИ / КУРСЫ / 04.02"
  const extractNameWithoutId = (name: string): string => {
    // Убираем ID в скобках в конце строки (формат: пробел + открывающая скобка + цифры + закрывающая скобка)
    return name.replace(/\s*\(\d+\)\s*$/, '').trim();
  };

  const confirmSelect = () => {
    const parsedInputData = listText.split('\n').map(s => s.trim()).filter(Boolean);
    
    if (parsedInputData.length === 0) {
      setOpen(false);
      return;
    }

    // Нормализуем введённые значения для поиска
    const normalizedInputs = parsedInputData.map(normalizeString);

    if (list && list.length > 0) {
      // Если список уже загружен — фильтруем локально
      // Находим все элементы по частичному совпадению (для отображения в списке)
      const partialMatchList = list.filter((item) => {
        const normalizedName = normalizeString(item.name);
        return normalizedInputs.some(input => normalizedName.includes(input));
      });

      // Находим элементы с 100% совпадением названия (для проставления галочек)
      const exactMatchList = list.filter((item) => {
        const nameWithoutId = extractNameWithoutId(item.name);
        const normalizedName = normalizeString(nameWithoutId);
        return normalizedInputs.some(input => normalizedName === input);
      });

      // setMainList — показывает все найденные по частичному совпадению
      // setList — проставляет галочки только на 100% совпадениях
      setMainList && setMainList(partialMatchList);
      setList(exactMatchList);

      setOpen(false);
      setListText('');
    } else {
      // Если списка нет — загружаем с сервера
      // Делаем отдельный запрос для каждого введённого значения, чтобы найти точные совпадения
      if (selectedSchool) {
        setIsLoading(true);

        // Создаём массив промисов для каждого введённого значения
        const requests = parsedInputData.map((inputValue) => {
          const data = {
            school_id: selectedSchool.id,
            name: id,
            str: inputValue, // Используем введённое значение как поисковый запрос
            values: [],
          };
          return axios.post(API_GET_FILTER_LIST, data, axiosConfigAuth());
        });

        Promise.all(requests)
          .then((responses) => {
            // Объединяем результаты всех запросов
            const allItems: any[] = [];
            const seenIds = new Set<string>();

            responses.forEach((response) => {
              if (response.status === 200 && response.data) {
                (response.data as any[]).forEach((item) => {
                  const itemId = String(item.id);
                  if (!seenIds.has(itemId)) {
                    seenIds.add(itemId);
                    // Нормализуем id к строке для корректного сравнения
                    allItems.push({ ...item, id: itemId });
                  }
                });
              }
            });

            // Находим элементы с 100% совпадением названия (для проставления галочек)
            const exactMatchList = allItems.filter((item) => {
              const nameWithoutId = extractNameWithoutId(item.name);
              const normalizedName = normalizeString(nameWithoutId);
              return normalizedInputs.some(input => normalizedName === input);
            });

            // setMainList — показывает все найденные с сервера (по частичному совпадению)
            // setList — проставляет галочки только на 100% совпадениях
            setMainList && setMainList(allItems);
            setList(exactMatchList);

            setOpen(false);
            setListText('');
          })
          .catch((err) => {
            handleResponseError(dispatch, err);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  };

  return  (
    <Popover
      title="Введите список значений"
      content={
        <div>
          <p>
            <Input.TextArea
              style={{ width: 300, height: 250 }}
              value={listText}
              onChange={(e) => setListText(e.target.value)}
            />
          </p>

          <div>
            <Button
              type="primary"
              onClick={() => confirmSelect()}
              loading={isLoading}
            >
              Применить
            </Button>
          </div>
        </div>
      }
      trigger="click"
      open={open}
      onOpenChange={(val) => setOpen(val)}
    >
      <Button size="small">Выбрать списком</Button>
    </Popover>
  );
};

export default ListTextSelector;
